import React from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigationStore, BreadcrumbItem } from '../../store/navigationStore';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from '../../shell/NotificationBell';
import { useSearchStore } from '../../store/searchStore';
import { useUiStore } from '../../store/uiStore';
import { navItemFor, routeForPage } from '../../shell/routes';
import { GROUPS, NAV_ITEMS } from '../../shell/GroupedNavRail';
import { useUiV2 } from '../../shell/uiFlags';

/** Group > Page for pages that set no breadcrumbs of their own (ui_v2), so
 *  the bar always says where you are instead of sitting empty. */
function useDefaultCrumbs(currentPage: string): BreadcrumbItem[] {
  const { t } = useTranslation();
  const route = routeForPage(currentPage);
  if (!route) return [];
  const item = NAV_ITEMS.find(i => i.path === navItemFor(currentPage));
  const group = GROUPS.find(g => g.id === route.group);
  const crumbs: BreadcrumbItem[] = [];
  if (group) crumbs.push({ label: t(group.i18nKey, group.label) });
  if (item) crumbs.push({ label: t(item.i18nKey, item.label), page: item.path });
  return crumbs;
}

export const Breadcrumb: React.FC = () => {
  const { t } = useTranslation();
  const { breadcrumbs: pageCrumbs, navigateTo, currentPage } = useNavigationStore();
  const uiV2 = useUiV2();
  const defaultCrumbs = useDefaultCrumbs(currentPage);
  const breadcrumbs = uiV2 && pageCrumbs.length === 0 ? defaultCrumbs : pageCrumbs;
  const openSearchModal = useSearchStore(s => s.open);
  const openCommandPalette = useUiStore(s => s.openCommandPalette);
  // ui_v2: one search. The button opens the same palette as Cmd+K.
  const open = uiV2 ? openCommandPalette : openSearchModal;

  return (
    <div data-tour="topbar" style={{
      height: uiV2 ? 40 : 34,
      display: 'flex',
      alignItems: 'center',
      padding: '0 12px 0 20px',
      borderBottom: '1px solid var(--color-border)',
      backgroundColor: 'var(--color-surface)',
      gap: 4,
      flexShrink: 0,
      overflow: 'hidden',
    }}>
      {breadcrumbs.map((crumb: BreadcrumbItem, i: number) => {
        const isLast = i === breadcrumbs.length - 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <ChevronRight size={12} style={{ color: 'var(--color-text-subtle)', flexShrink: 0 }} />
            )}
            {!isLast && crumb.page ? (
              <button
                onClick={() => navigateTo(crumb.page!)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0 2px',
                  fontSize: 12,
                  color: 'var(--color-text-muted)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'color 80ms',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = 'var(--color-text)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)'; }}
              >
                {crumb.label}
              </button>
            ) : (
              <span style={{
                fontSize: 12,
                fontWeight: isLast ? 500 : 400,
                color: isLast ? 'var(--color-text)' : 'var(--color-text-muted)',
                padding: '0 2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}

      {/* Search trigger */}
      <button
        data-tour="global-search"
        onClick={open}
        style={{
          marginLeft: 'auto',
          display: 'flex', alignItems: 'center', gap: 6,
          height: 24, padding: '0 10px',
          border: '1px solid var(--color-border)',
          borderRadius: uiV2 ? 'var(--radius-control)' : 5,
          backgroundColor: 'var(--color-base)',
          cursor: 'pointer',
          fontSize: 11, color: 'var(--color-text-muted)',
          flexShrink: 0,
          transition: 'border-color 80ms, background-color 80ms',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-interactive)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-interactive-dim)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border)'; (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-base)'; }}
      >
        <Search size={11} />
        <span>{t('common.search', 'Search')}</span>
      </button>

      {/* Bell + theme always in top-right of this bar */}
      <div style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
        <ThemeToggle />
        <NotificationBell />
      </div>
    </div>
  );
};

export default Breadcrumb;
